import React from 'react'
import styled, { keyframes } from 'styled-components'
import { surface, line, radius, shadow, bp, cardStyle } from 'features/dashboard/tokens'

const DashboardSkeleton = () => (
  <Wrap>
    <Grid>
      <Main>
        <HeroBox>
          <HeroBody>
            <Line $w='90px' $h='10px' />
            <Line $w='62%' $h='28px' />
            <Line $w='40%' $h='12px' />
            <StatRow>
              <Line $w='52px' $h='28px' />
              <Line $w='52px' $h='28px' />
              <Line $w='52px' $h='28px' />
            </StatRow>
          </HeroBody>
          <Donut />
        </HeroBox>

        <SectionBox>
          <Line $w='130px' $h='10px' />
          <DomainGrid>
            {Array.from({ length: 6 }).map((_, i) => (
              <DomainTile key={i}>
                <Line $w='32px' $h='32px' $round />
                <Line $w='80%' $h='14px' />
                <Line $w='40%' $h='28px' />
                <Line $w='100%' $h='6px' />
              </DomainTile>
            ))}
          </DomainGrid>
        </SectionBox>

        <OpsRow>
          <OpsBox>
            <Line $w='80px' $h='12px' />
            <StatRow>
              {Array.from({ length: 4 }).map((_, i) => (
                <Tile key={i}>
                  <Line $w='40px' $h='22px' />
                  <Line $w='60px' $h='8px' />
                </Tile>
              ))}
            </StatRow>
          </OpsBox>
          <OpsBox>
            <Line $w='80px' $h='12px' />
            <StatRow>
              {Array.from({ length: 4 }).map((_, i) => (
                <Tile key={i}>
                  <Line $w='40px' $h='22px' />
                  <Line $w='60px' $h='8px' />
                </Tile>
              ))}
            </StatRow>
          </OpsBox>
        </OpsRow>
      </Main>

      <Rail>
        {Array.from({ length: 3 }).map((_, i) => (
          <RailBox key={i}>
            <Line $w='110px' $h='10px' />
            <Line $w='100%' $h='160px' style={{ marginTop: 10 }} />
            <Line $w='90%' $h='12px' style={{ marginTop: 10 }} />
            <Line $w='70%' $h='12px' style={{ marginTop: 6 }} />
            <Line $w='80%' $h='12px' style={{ marginTop: 6 }} />
          </RailBox>
        ))}
      </Rail>
    </Grid>
  </Wrap>
)

export default DashboardSkeleton

const shimmer = keyframes`
  0%   { background-position: -480px 0; }
  100% { background-position: 480px 0; }
`

const Wrap = styled.div`
  padding: 24px 32px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: ${bp.mobile}) {
    padding: 4px 0 16px;
    overflow-x: hidden;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  @media (max-width: ${bp.tablet}) { grid-template-columns: minmax(0, 1fr); }
`

const Main = styled.div` min-width: 0; display: flex; flex-direction: column; gap: 16px; `
const Rail = styled.aside` min-width: 0; display: flex; flex-direction: column; gap: 14px; `

const HeroBox = styled.section`
  position: relative;
  padding: 24px 28px;
  background: #D24724;
  color: #fff;
  border-radius: ${radius.md};
  box-shadow: ${shadow.lift};
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 28px;
  align-items: center;
  min-height: 180px;
  overflow: hidden;

  @media (max-width: ${bp.mobile}) {
    grid-template-columns: minmax(0, 1fr);
    justify-items: start;
    padding: 18px 16px;
    gap: 18px;
    min-height: 0;
  }
`

const HeroBody = styled.div`
  display: flex; flex-direction: column; gap: 10px; min-width: 0;
`

const Donut = styled.div`
  width: 140px; height: 140px;
  border-radius: ${radius.round};
  background: rgba(255, 255, 255, 0.22);
  flex-shrink: 0;

  @media (max-width: ${bp.mobile}) { width: 96px; height: 96px; }
`

const StatRow = styled.div`
  display: flex; gap: 16px;
  margin-top: 10px;
  min-width: 0;

  @media (max-width: ${bp.mobile}) { flex-wrap: wrap; gap: 10px; }
`

const Tile = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  flex: 1 1 90px;
  min-width: 0;
  padding: 10px 8px;
  background: ${surface.wash};
  border-radius: ${radius.md};
`

const SectionBox = styled.section`
  ${cardStyle}
  padding: 18px 22px;
  display: flex; flex-direction: column; gap: 14px;
  min-width: 0;

  @media (max-width: ${bp.mobile}) { padding: 14px 12px; }
`

const DomainGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;

  @media (max-width: ${bp.mobile}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
`

const DomainTile = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  padding: 14px;
  border-radius: ${radius.md};
  background: ${surface.wash};
`

const OpsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: ${bp.mobile}) { grid-template-columns: 1fr; }
`

const OpsBox = styled.section`
  ${cardStyle}
  padding: 16px 18px;
  display: flex; flex-direction: column; gap: 12px;
  min-width: 0;

  @media (max-width: ${bp.mobile}) { padding: 14px 12px; }
`

const RailBox = styled.section`
  ${cardStyle}
  padding: 16px 18px;
  min-width: 0;

  @media (max-width: ${bp.mobile}) { padding: 14px 12px; }
`

const Line = styled.span`
  display: block;
  width: ${({ $w }) => $w || '100%'};
  height: ${({ $h }) => $h || '12px'};
  border-radius: ${({ $round }) => $round ? radius.round : radius.sm};
  background: linear-gradient(90deg, ${line.base} 0%, ${surface.wash} 50%, ${line.base} 100%);
  background-size: 960px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`
